// Exaltation Forge: abas Fusão / Transferência / Conversão. Visual de forja exaltada (azul-arcano + dourado, cristais):
// grade de itens com tier em estrelas, o item em destaque com os dois espaços e o resultado, medidor de chance, opções
// com exalted core como cards e custos com "tem X / precisa Y". Facilidade do idle: os itens equipados e os da mochila
// aparecem direto na grade. CSS em client/css-parts/forge.css (prefixo .fx-).
import { useState } from 'preact/hooks';
import type { ItemInstance } from '../../../../shared/src/game/items';
import type { Extras } from '../../../../shared/src/game/extras';
import { CLASS_MAX_TIER, FORGE, FORGE_PRICE, tierChance } from '../../../../shared/src/game/forge';
import type { TierEffect } from '../../../../shared/src/game/forge';
import { catalogNow } from '../items';
import { ItemImg } from './ItemSlot';

const IMG = '/assets/interface/images';
const n = (v: number) => v.toLocaleString('pt-BR');
const pct2 = (v: number) => v.toFixed(2).replace('.', ',') + '%';
/** gold compacto (25 mil, 1,2 mi, 2,5 bi) */
const short = (v: number) => v >= 1e9 ? (Math.round(v / 1e8) / 10).toString().replace('.', ',') + ' bi' : v >= 1e6 ? (Math.round(v / 1e5) / 10).toString().replace('.', ',') + ' mi' : v >= 1e3 ? Math.round(v / 1e3) + ' mil' : String(v);
type Send = (m: { op: 'fusion' | 'transfer' | 'slivers' | 'core'; a?: number; b?: number; core1?: boolean; core2?: boolean }) => void;
type Tab = 'fusion' | 'transfer' | 'conversion';

/** efeito de tier por posição do equipamento (o mesmo do texto de look do item) */
const EFFECT: Record<string, [string, TierEffect, string]> = {
  weapon: ['Onslaught', 'onslaught', '+60% de dano'], armor: ['Ruse', 'ruse', 'esquiva'], head: ['Momentum', 'momentum', 'tira 2 s das recargas'],
  legs: ['Transcendence', 'transcendence', 'avatar por 7 s'], feet: ['Amplification', 'amplification', 'aumenta os outros efeitos'],
};
const SEEN_KEY = 'nx-help-forge';

function Cur(props: { icon: 'dust' | 'sliver' | 'exaltedcore'; v: number | string; bad?: boolean }) {
  return <span class={'fx-cur' + (props.bad ? ' bad' : '')}><img src={`${IMG}/icon-currency-${props.icon}.png`} alt="" />{props.v}</span>;
}

/** tier em estrelas: douradas até o tier atual, apagadas até o máximo da classe; `next` brilha */
function Stars(props: { tier: number; max: number; next?: number }) {
  if (!props.max) return null;
  return (
    <span class="fx-stars" title={`Tier ${props.tier} de ${props.max}`}>
      {Array.from({ length: props.max }, (_, i) => <i key={i} class={i < props.tier ? 'on' : props.next !== undefined && i < props.next ? 'nx' : ''} />)}
    </span>
  );
}

/** linha de material: ícone, tem / precisa, verde ou vermelho */
function Need(props: { icon: string; label: string; have: number; need: number; fmt?: (v: number) => string }) {
  const ok = props.have >= props.need, f = props.fmt ?? n;
  return (
    <div class={'fx-need' + (ok ? ' ok' : ' bad')}>
      <img src={props.icon} alt="" />
      <span class="fx-need-l">{props.label}</span>
      <span class="fx-need-v"><b>{f(props.have)}</b> / {f(props.need)}</span>
      <i>{ok ? '✔' : '✖'}</i>
    </div>
  );
}

export function Forge(props: { items: ItemInstance[]; bank: number; extras: Extras; send: Send }) {
  const cat = catalogNow();
  const byId = new Map(props.items.map((i) => [i.id, i]));
  const carried = (i: ItemInstance) => { let c: ItemInstance | undefined = i; while (c && c.place === 'container') c = c.parentId ? byId.get(c.parentId) : undefined; return !!c && c.place === 'equip'; };
  const list = props.items.filter((i) => carried(i) && (cat?.get(i.typeId)?.tierClass ?? 0) > 0);
  const [tab, setTab] = useState<Tab>('fusion');
  const [a, setA] = useState<number | null>(null);
  const [b, setB] = useState<number | null>(null);
  const [c1, setC1] = useState(false), [c2, setC2] = useState(false);
  const [onlyPairs, setOnlyPairs] = useState(false);
  const x = props.extras;
  const dust = x.dust ?? 0, slivers = x.slivers ?? 0, cores = x.cores ?? 0;
  // passo a passo: abre sozinho enquanto o jogador nunca usou a forja (nenhum item com tier e nenhum core) e não fechou a ajuda
  const fresh = !list.some((i) => (i.tier ?? 0) > 0) && !cores;
  const [help, setHelp] = useState(() => { if (!fresh) return false; try { return localStorage.getItem(SEEN_KEY) !== '1'; } catch { return true; } });
  const toggleHelp = () => { const v = !help; setHelp(v); if (!v) { try { localStorage.setItem(SEEN_KEY, '1'); } catch { /* sem storage */ } } };

  const A = list.find((i) => i.id === a), B = list.find((i) => i.id === b);
  const ta = A ? cat?.get(A.typeId) : undefined;
  const partnerOf = (src: ItemInstance, mode: Tab) => list.filter((i) => i.id !== src.id && (mode === 'fusion'
    ? i.typeId === src.typeId && (i.tier ?? 0) === (src.tier ?? 0)
    : cat?.get(i.typeId)?.tierClass === cat?.get(src.typeId)?.tierClass && cat?.get(i.typeId)?.slot === cat?.get(src.typeId)?.slot && !i.tier));
  const sources = list.filter((i) => tab === 'fusion' ? (!onlyPairs || partnerOf(i, 'fusion').length > 0) && (i.tier ?? 0) < (CLASS_MAX_TIER[cat?.get(i.typeId)?.tierClass ?? 0] ?? 0) : (i.tier ?? 0) >= 2);
  const pairs = A ? partnerOf(A, tab) : [];
  const cls = ta?.tierClass ?? 0, tier = A?.tier ?? 0;
  const maxT = CLASS_MAX_TIER[cls] ?? 0;
  const price = tab === 'fusion' ? FORGE_PRICE[cls]?.[tier] : FORGE_PRICE[cls]?.[tier - 2];
  const chance = FORGE.baseChance + (c1 ? FORGE.coreBonus : 0);
  const pick = (i: ItemInstance) => { setA(i.id); const p = partnerOf(i, tab); setB(p[0]?.id ?? null); };
  const dustNeed = tab === 'fusion' ? FORGE.fusionDust : FORGE.transferDust;
  const coreNeed = tab === 'fusion' ? (c1 ? 1 : 0) + (c2 ? 1 : 0) : (price?.[1] ?? 0);
  const costOk = price ? props.bank >= price[0] && dust >= dustNeed && cores >= coreNeed : false;
  const maxed = tab === 'fusion' && A && tier >= maxT;
  const resTier = A && B ? (tab === 'fusion' ? tier + 1 : tier - 1) : undefined;
  const effOf = (i: ItemInstance | undefined) => (i ? EFFECT[cat?.get(i.typeId)?.slot ?? ''] : undefined);
  const eff = effOf(tab === 'fusion' ? A : B);

  const cell = (i: ItemInstance, on: boolean, click: () => void) => {
    const t = cat?.get(i.typeId), mx = CLASS_MAX_TIER[t?.tierClass ?? 0] ?? 0, hasPair = tab === 'fusion' && partnerOf(i, 'fusion').length > 0;
    return (
      <button key={i.id} class={'fx-cell' + (on ? ' on' : '') + (hasPair ? ' pair' : '')} onClick={click}
        title={`${t?.name} · classe ${t?.tierClass} · tier ${i.tier ?? 0}/${mx}${hasPair ? ' · tem par para fusão' : ''}`}>
        <span class="fx-cell-img"><ItemImg typeId={i.typeId} count={1} /></span>
        <i class="fx-cls">C{t?.tierClass}</i>
        {(i.tier ?? 0) > 0 && <i class="fx-tier">T{i.tier}</i>}
        {i.place === 'equip' && <i class="fx-eq" title="Equipado">E</i>}
      </button>
    );
  };
  const slot = (i: ItemInstance | undefined, label: string, tierShow: number | undefined, kind: string) => (
    <div class={'fx-slot ' + kind + (i ? ' full' : '')}>
      <div class="fx-box">
        {i ? <ItemImg typeId={i.typeId} count={1} /> : <span class="fx-empty">?</span>}
        {i && tierShow !== undefined && <i class="fx-tier">T{tierShow}</i>}
      </div>
      <small>{label}</small>
    </div>
  );
  const switchTab = (id: Tab) => { setTab(id); setA(null); setB(null); setC1(false); setC2(false); };

  const sliverCost = FORGE.dustPerSliver * FORGE.sliverBatch;

  return (
    <div class="fx">
      {/* cabeçalho: cristal exaltado girando entre faíscas douradas */}
      <div class="fx-head">
        <div class="fx-crystal" aria-hidden="true">
          <span class="fx-gem" />
          <i class="fx-spark s1" /><i class="fx-spark s2" /><i class="fx-spark s3" /><i class="fx-spark s4" />
        </div>
        <div class="fx-head-txt">
          <div class="fx-title">Forja Exaltada
            <button class={'fx-help' + (help ? ' on' : '')} onClick={toggleHelp} title="Como funciona">?</button></div>
          <div class="fx-sub">Funda itens iguais para subir o <b>tier</b> · transfira o tier para um item novo</div>
          <div class="fx-wallet">
            <span class="fx-chip dust" title={`Dust (limite de ${FORGE.maxDust})`}>
              <img src={`${IMG}/icon-currency-dust.png`} alt="" /><b>{dust}</b>/{FORGE.maxDust}
              <span class="fx-mini"><em style={{ width: Math.min(100, dust / FORGE.maxDust * 100) + '%' }} /></span>
            </span>
            <span class="fx-chip sliver" title={`${FORGE.coreSlivers} slivers formam um exalted core`}>
              <img src={`${IMG}/icon-currency-sliver.png`} alt="" /><b>{slivers}</b>
              <span class="fx-mini"><em style={{ width: Math.min(100, slivers / FORGE.coreSlivers * 100) + '%' }} /></span>
            </span>
            <span class="fx-chip core" title="Exalted cores"><img src={`${IMG}/icon-currency-exaltedcore.png`} alt="" /><b>{cores}</b></span>
            <span class="fx-chip gold" title="O gold sai do banco"><img src={`${IMG}/icon-goldcoin.png`} alt="" /><b>{n(props.bank)}</b> gp</span>
          </div>
        </div>
      </div>

      {help && (
        <div class="fx-steps">
          <div><i>⚔️</i><b>1. Junte dois iguais</b><small>Itens com classe de tier (C1 a C4) que estejam com você. A fusão pede dois itens do mesmo tipo e do mesmo tier.</small></div>
          <div><i>💎</i><b>2. Funda</b><small>{FORGE.baseChance}% de sucesso (+{FORGE.coreBonus}% com um exalted core). Na falha, um item some e o outro perde 1 tier ({FORGE.tierLossReduction}% de chance de não perder com outro core).</small></div>
          <div><i>🔁</i><b>3. Transfira</b><small>O tier de um item (tier 2 ou mais) passa para outro da mesma classe e posição, com 1 tier a menos.</small></div>
          <div><i>✨</i><b>4. Recursos</b><small>Dust e slivers caem dos monstros Super, Mega e Ultra. {FORGE.dustPerSliver} dust = 1 sliver; {FORGE.coreSlivers} slivers = 1 core.</small></div>
        </div>
      )}

      <div class="fx-tabs">
        {([['fusion', 'Fusão', 'icon-fusion'], ['transfer', 'Transferência', 'icon-transfer'], ['conversion', 'Conversão', 'icon-currency-sliver']] as const).map(([id, l, ic]) =>
          <button key={id} class={'fx-tab' + (tab === id ? ' on' : '')} onClick={() => switchTab(id)}>
            <img src={`${IMG}/${ic}.png`} alt="" />{l}</button>)}
      </div>

      {tab === 'conversion' ? (
        <div class="fx-conv">
          <div class={'fx-card' + (dust >= sliverCost ? ' ready' : '')}>
            <div class="fx-card-h">Dust → Slivers</div>
            <div class="fx-conv-row"><Cur icon="dust" v={sliverCost} bad={dust < sliverCost} /><span class="fx-arrow">➜</span><Cur icon="sliver" v={FORGE.sliverBatch} /></div>
            <div class="fx-bar"><i style={{ width: Math.min(100, dust / sliverCost * 100) + '%' }} /><span>{dust} / {sliverCost} dust</span></div>
            <button class="tb-button green" disabled={dust < sliverCost} onClick={() => props.send({ op: 'slivers' })}>Converter</button>
          </div>
          <div class={'fx-card' + (slivers >= FORGE.coreSlivers ? ' ready' : '')}>
            <div class="fx-card-h">Slivers → Exalted Core</div>
            <div class="fx-conv-row"><Cur icon="sliver" v={FORGE.coreSlivers} bad={slivers < FORGE.coreSlivers} /><span class="fx-arrow">➜</span><Cur icon="exaltedcore" v={1} /></div>
            <div class="fx-bar"><i style={{ width: Math.min(100, slivers / FORGE.coreSlivers * 100) + '%' }} /><span>{slivers} / {FORGE.coreSlivers} slivers</span></div>
            <button class="tb-button green" disabled={slivers < FORGE.coreSlivers} onClick={() => props.send({ op: 'core' })}>Converter</button>
          </div>
          <div class="fx-card fx-info">
            <div class="fx-card-h">Efeitos de tier <small>chance no tier 1 / 5 / 10</small></div>
            {(['weapon', 'armor', 'head', 'legs', 'feet'] as const).map((s) => {
              const [nm, key, desc] = EFFECT[s]!;
              const pos = { weapon: 'Arma', armor: 'Armadura', head: 'Capacete', legs: 'Pernas', feet: 'Botas' }[s];
              return (
                <div key={s} class="fx-eff">
                  <span><b>{nm}</b><small>{pos} — {desc}</small></span>
                  <em>{[1, 5, 10].map((t) => tierChance(key, t).toFixed(2)).join(' / ')}%</em>
                </div>
              );
            })}
            <small class="muted">Dust e slivers caem dos monstros especiais das hunts (Super, Mega e Ultra fazem o papel dos Influenced e Fiendish do Tibia).</small>
          </div>
        </div>
      ) : (
        <div class="fx-main">
          <div class="fx-sel">
            <div class="fx-cap">Seus itens <small>{sources.length}</small></div>
            {tab === 'fusion' && <label class="fx-check"><input type="checkbox" checked={onlyPairs} onChange={() => setOnlyPairs(!onlyPairs)} /> Só itens com par para fusão</label>}
            <div class="fx-grid">{sources.map((i) => cell(i, i.id === a, () => pick(i)))}</div>
            {!sources.length && <p class="muted small fx-empty-txt">{tab === 'fusion' ? 'Nenhum item com classe de tier com você.' : 'Nenhum item de tier 2 ou mais com você.'}</p>}
            {A && pairs.length > 1 && <>
              <div class="fx-cap">{tab === 'fusion' ? 'Segundo item' : 'Item de destino'} <small>{pairs.length}</small></div>
              <div class="fx-grid">{pairs.map((i) => cell(i, i.id === b, () => setB(i.id)))}</div></>}
          </div>

          <div class="fx-detail">
            {!A ? (
              <div class="fx-idle">
                <img src={`${IMG}/${tab === 'fusion' ? 'icon-fusion' : 'icon-transfer'}.png`} alt="" />
                <b>{tab === 'fusion' ? 'Escolha um item para fundir' : 'Escolha o item de origem'}</b>
                <small>{tab === 'fusion' ? 'Itens com borda dourada já têm um par igual para a fusão.' : 'O tier passa para outro item da mesma classe e do mesmo tipo (tier 0), com 1 tier a menos. O item de origem some.'}</small>
              </div>
            ) : <>
              <div class="fx-hero">
                <div class="fx-hero-txt">
                  <b class="fx-name">{ta?.name}</b>
                  <div class="fx-tags"><span class="fx-tag">Classe {cls}</span><span class="fx-tag">Tier máx. {maxT}</span>{A.place === 'equip' && <span class="fx-tag eq">equipado</span>}</div>
                  <Stars tier={tier} max={maxT} next={tab === 'fusion' && !maxed && B ? tier + 1 : undefined} />
                </div>
              </div>

              <div class="fx-slots">
                {slot(A, tab === 'fusion' ? 'Item' : 'Origem', tier, 'src')}
                <img class="fx-op" src={`${IMG}/${tab === 'fusion' ? 'icon-fusion' : 'icon-transfer'}.png`} alt="" />
                {slot(B, tab === 'fusion' ? 'Par' : 'Destino', B ? (B.tier ?? 0) : undefined, 'pair')}
                <img class="fx-op" src={`${IMG}/icon-arrow-rightlarge${A && B ? '-filled' : ''}.png`} alt="" />
                {slot(A && B ? (tab === 'fusion' ? A : B) : undefined, 'Resultado', resTier, 'res')}
              </div>
              {!B && <p class="fx-warn">{tab === 'fusion' ? 'Falta um segundo item igual e do mesmo tier para a fusão.' : 'Falta um item de destino da mesma classe e posição com tier 0.'}</p>}
              {maxed && <p class="fx-warn">Tier máximo da classe.</p>}

              {eff && resTier !== undefined && !maxed && (
                <div class="fx-effect">
                  <span><b>{eff[0]}</b> <small>({eff[2]})</small></span>
                  <span class="fx-eff-jump">{pct2(tierChance(eff[1], tab === 'fusion' ? tier : (B?.tier ?? 0)))} <i>➜</i> <b>{pct2(tierChance(eff[1], resTier))}</b></span>
                </div>
              )}

              {tab === 'fusion' ? <>
                <div class={'fx-meter' + (c1 ? ' up' : '')}>
                  <div class="fx-meter-top"><span>Chance de sucesso</span><b>{A && B ? `${chance}%` : '—'}</b></div>
                  <div class="fx-gauge"><i style={{ width: (A && B ? chance : 0) + '%' }} /></div>
                </div>
                <div class="fx-opts">
                  <button class={'fx-opt' + (c1 ? ' on' : '') + (cores < 1 ? ' dim' : '')} onClick={() => setC1(!c1)}>
                    <span class="fx-opt-h"><input type="checkbox" checked={c1} tabIndex={-1} readOnly /> <Cur icon="exaltedcore" v={1} /></span>
                    <b>+{FORGE.coreBonus}% de chance</b><small>Gasta 1 exalted core</small>
                  </button>
                  <button class={'fx-opt' + (c2 ? ' on' : '') + (cores < 1 ? ' dim' : '')} onClick={() => setC2(!c2)}>
                    <span class="fx-opt-h"><input type="checkbox" checked={c2} tabIndex={-1} readOnly /> <Cur icon="exaltedcore" v={1} /></span>
                    <b>Proteger o tier</b><small>{FORGE.tierLossReduction}% de não perder tier na falha</small>
                  </button>
                </div>
                <small class="fx-note">Na falha, um dos itens some e o outro perde 1 tier (como no Tibia).</small>
              </> : <small class="fx-note">O tier passa para outro item da mesma classe e do mesmo tipo (tier 0), com 1 tier a menos. O item de origem some.</small>}

              <div class="fx-cost">
                <div class="fx-cap">Custo</div>
                {A && B && price && !maxed ? <>
                  <Need icon={`${IMG}/icon-goldcoin.png`} label="Gold (banco)" have={props.bank} need={price[0]} fmt={short} />
                  <Need icon={`${IMG}/icon-currency-dust.png`} label="Dust" have={dust} need={dustNeed} />
                  {coreNeed > 0 && <Need icon={`${IMG}/icon-currency-exaltedcore.png`} label="Exalted cores" have={cores} need={coreNeed} />}
                  <button class={'fx-go' + (costOk ? ' ready' : '')} disabled={!costOk} onClick={() => props.send(tab === 'fusion' ? { op: 'fusion', a: A.id, b: B.id, core1: c1, core2: c2 } : { op: 'transfer', a: A.id, b: B.id })}>
                    <img src={`${IMG}/button-exaltation-forge-up.png`} alt="" />{tab === 'fusion' ? 'Fundir' : 'Transferir'}
                    <small>{n(price[0])} gp</small></button>
                  {!costOk && <small class="fx-why">Faltam recursos (veja as linhas em vermelho).</small>}
                </> : <small class="muted">{maxed ? 'Este item já está no tier máximo.' : 'Escolha os itens.'}</small>}
              </div>
            </>}
          </div>
        </div>
      )}
    </div>
  );
}
